import React, { Component } from 'react';
import ProductImage, { ImagePair } from '../images/product_image';
import { fileWithoutExtension } from '../../helpers/paths';
import Header from '../header';
import Footer from '../footer';
import Attributes, { ProductAttribute } from './attributes';
import Similar from './similar';
import Description from './description';
import Notification from '../notification';
import Overview from './overview';
import { Supplier } from '../../rpc/admin/suppliers_pb';
import { Image } from '../../rpc/admin/image_pb';
import { Hit } from '../../rpc/admin/search_pb';
import Helmet from 'react-helmet';

export class DisplayProduct {
    supplier: Supplier = Supplier.SUPPLIER_UNKNOWN;
    sku: string = '';
    title: string = '';
    description: string = '';
    manufacturer: string = '';
    images: Image[] = [];
    price: number = 0;
    mapp: number = 0;
    msrp: number = 0;
    qoh: number = 0;
    attributes: ProductAttribute[] = [];
}

interface ProductProps {
    product: DisplayProduct;
    images: Image[];
    supplierProducts: Hit[];
    imagePrefix: string;
    manufacturerFilterPrefix: string;
    errorMessage: string;
}

interface ProductState {}

export abstract class Product extends Component<ProductProps, ProductState> {

    // Given a list of original images, remove duplicates.
    static pruneImages(images: Image[]): Image[] {
        let result: Image[] = [];

        images.forEach((image) => {
            result.push(image);
        });

        return result;
    }

    static originalImages(imagePrefix: string, images: Image[]): Image[] {
        const originalPrefix = imagePrefix + "/originals/";
        const originals: Image[] = [];
        images.forEach(image => {
            if (image.getPath().startsWith(originalPrefix)) {
                originals.push(image);
            }
        });
        return originals;
    }

    static maybeAddStringAttribute(product: DisplayProduct, key: string, value: string) {
        if (key && value) {
            if (!product.attributes) {
                product.attributes = [];
            }
            product.attributes.push({
                key: key,
                value: value,
            })
        }
    }

    static addBooleanAttribute(product: DisplayProduct, key: string, value: boolean | null) {
        if (value != null) {
            if (!product.attributes) {
                product.attributes = [];
            }
            product.attributes.push({
                key: key,
                value: value ? 'No' : 'Yes',
            });
        }
    }

    static addNumberAttribute(product: DisplayProduct, key: string, value: number | null) {
        if (value != null) {
            if (!product.attributes) {
                product.attributes = [];
            }
            product.attributes.push({
                key: key,
                value: value.toString(),
            });
        }
    }

    render() {
        const thumbnailPrefix = this.props.imagePrefix + "/128x128/";
        const images: ImagePair[] = [];
        this.props.images.forEach(image => {
            const thumbnailPath = thumbnailPrefix + fileWithoutExtension(image.getPath()) + ".jpg";
            for (let i = 0; i < this.props.product.images.length; i++) {
                const thumbnail = this.props.product.images[i];
                if (thumbnail.getPath() === thumbnailPath) {
                    images.push({
                        original: image,
                        thumbnail: thumbnail,
                        alt: this.props.product.title
                    });
                    break;
                }
            }
        });

        return (
            <>
                <Helmet>
                    <title>{this.props.product.title} | Dashboard</title>
                </Helmet>

                <Header />

                <div className="container">
                    <Notification className="product-notification" message={this.props.errorMessage} />

                    <div className="columns product-top">
                        <div className="column">
                            <ProductImage images={images} />
                        </div>
                        <div className="column">
                            <Overview product={this.props.product} manufacturerFilterPrefix={this.props.manufacturerFilterPrefix} supplierProducts={this.props.supplierProducts} />
                        </div>
                    </div>

                    <div className="product-content">
                        <Description description={this.props.product.description} />
                        <Attributes attributes={this.props.product.attributes} />
                        <Similar hits={this.props.supplierProducts} price={this.props.product.price!} qoh={this.props.product.qoh!} />
                    </div>
                </div>

                <Footer />
            </>
        );
    }
}
